import type {CurrentProject} from 'common/report/model-resources/projects';
import {ProjectDetails} from 'common/report/stores/project-details';
import {
  getOrgId,
  getProjectId,
  isCurrentProjectInternal,
  isUrlDefinedExperimentalFeatureEnabled,
} from 'common/report/util';
import {isDevEnv, isTestEnv} from 'common/report/util/env';
import {Location} from 'common/types/globals/location';

import {ProjectFeatureFlag} from '../constants';

export const featureSamplingUtils = {
  determineFeatureEligibility,
};

// TODO(Home): Clean up after Home is 100% available.
const HOME_BETA_TESTER_USER_IDS = [
  3920933,
  2596976,
  2336228,
  2019561,
  2760331,
  4123676,
  3695393,
  1830665,
  3299256,
  3232241,
  3569145,
  3017239,
];

/**
 * Check deterministically whether a user or group is included in a feature rollout/test based on a
 * unique user/group string identifier and the desired percentage to include.
 */
function determineFeatureEligibility(
  userOrGroupId: string,
  feature: string,
  percentAllowed: number /* must be in range [0, 100] */,
): boolean {
  if (!(percentAllowed >= 0 && percentAllowed <= 100)) {
    throw new Error(`@percentAllowed argument must be in range [0, 100] inclusive`);
  }

  if (typeof userOrGroupId === `undefined` || userOrGroupId === null || userOrGroupId === ``) {
    throw new Error(`@userOrGroupId argument cannot be null or undefined or empty`);
  }
  if (typeof feature === `undefined` || feature === null || feature === ``) {
    throw new Error(`@feature argument cannot be null or undefined or empty`);
  }

  switch (percentAllowed) {
    case 0:
      return false;
    case 100:
      return true;
    default:
      break;
  }

  const key = userOrGroupId + feature;

  // Bernstein's hash: http://www.cse.yorku.ca/~oz/hash.html#djb2
  // The djb2 hash was chosen because of the claim that "it has excellent distribution and speed on many different sets of keys and table sizes",
  // but we have not validated whether it is statistically reliable and accurate to use in sampling users/groups for rollouts and analysis.
  let hash = 5381;
  for (let i = 0; i < key.length; i++) {
    hash = (hash << 5) + hash + key.charCodeAt(i);
    hash = hash & hash;
  }
  const dart = (hash >>> 0) % 100;
  return dart < percentAllowed;
}

enum FeatureRolloutType {
  Project,
  Org,
}
class FeatureRollout {
  private type: FeatureRolloutType;
  private percentage: number;
  private featureflag: ProjectFeatureFlag;

  public name: FeatureRolloutName;

  constructor(params: {
    type: FeatureRolloutType;
    name: FeatureRolloutName;
    percentage: number;
    featureflag?: ProjectFeatureFlag;
  }) {
    Object.assign(this, params);
  }

  isEnabled(project: CurrentProject | ProjectDetails | null): boolean {
    if (this.type === FeatureRolloutType.Project) {
      const projectId = project?.id.toString() ?? getProjectId();
      return (
        featureSamplingUtils.determineFeatureEligibility(projectId, this.name.toString(), this.percentage) ||
        project?.hasFeatureFlag(this.featureflag)
      );
    } else {
      const orgId = project?.organizationId.toString();
      return orgId
        ? featureSamplingUtils.determineFeatureEligibility(orgId, this.name.toString(), this.percentage) ||
            project?.hasFeatureFlag(this.featureflag)
        : false;
    }
  }
}

// Add any gradual feature rollouts here
export enum FeatureRolloutName {
  CleanerUrls = `cleaner-urls`,
  DashboardUndoRedo = `dashboard-undo-redo`,
  InsightsPivotTable = `insights-pivot-table`,
  OnboardingChecklist = `onboarding-checklist`,
  InsightsNewCountMenu = `insights-new-count-menu`,
  CartAnalysis = `cart-analysis`,
  BetterBrokenState = `better-broken-state`,
  MultiAttribution = `multi-touch-attribution`,
  MultiAttributionFilter = `multi-touch-attribution-filter`,
  UniqueUniques = `unique-uniques`,
  RHSMode = `rhs-mode`,
  HomePrototype = `home-prototype`,
  PageDuration = `page-duration`,
  WebAnalyticsDashboardAndChannelClassifier = `web-analytics-dashboard-and-channel-classifier`,
}

export const FeatureRollouts: {[key in FeatureRolloutName]: FeatureRollout} = {
  [FeatureRolloutName.CleanerUrls]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.CleanerUrls,
    percentage: 100,
  }),
  [FeatureRolloutName.DashboardUndoRedo]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.DashboardUndoRedo,
    percentage: 100,
  }),
  [FeatureRolloutName.InsightsPivotTable]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.InsightsPivotTable,
    percentage: 0,
  }),
  [FeatureRolloutName.OnboardingChecklist]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.OnboardingChecklist,
    percentage: 0,
  }),
  [FeatureRolloutName.InsightsNewCountMenu]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.InsightsNewCountMenu,
    featureflag: ProjectFeatureFlag.InsightsNewCountMenu,
    percentage: 0,
  }),
  [FeatureRolloutName.CartAnalysis]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.CartAnalysis,
    featureflag: ProjectFeatureFlag.CartAnalysis,
    percentage: 100,
  }),
  [FeatureRolloutName.BetterBrokenState]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.BetterBrokenState,
    featureflag: ProjectFeatureFlag.BetterBrokenState,
    percentage: 100,
  }),
  [FeatureRolloutName.MultiAttribution]: new FeatureRollout({
    type: FeatureRolloutType.Project,
    name: FeatureRolloutName.MultiAttribution,
    featureflag: ProjectFeatureFlag.MultiAttribution,
    percentage: 100,
  }),

  [FeatureRolloutName.MultiAttributionFilter]: new FeatureRollout({
    type: FeatureRolloutType.Project,
    name: FeatureRolloutName.MultiAttributionFilter,
    featureflag: ProjectFeatureFlag.MultiAttributionFilter,
    percentage: 100,
  }),
  [FeatureRolloutName.UniqueUniques]: new FeatureRollout({
    type: FeatureRolloutType.Project,
    name: FeatureRolloutName.UniqueUniques,
    featureflag: ProjectFeatureFlag.UniqueUniques,
    percentage: 100,
  }),
  [FeatureRolloutName.RHSMode]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.RHSMode,
    percentage: 0, // TODO(RHS): tweak this on launch
  }),
  // TODO(Home): Clean up after prototype has finished running.
  [FeatureRolloutName.HomePrototype]: new FeatureRollout({
    type: FeatureRolloutType.Org,
    name: FeatureRolloutName.HomePrototype,
    featureflag: ProjectFeatureFlag.HomePage,
    percentage: 0,
  }),
  [FeatureRolloutName.WebAnalyticsDashboardAndChannelClassifier]: new FeatureRollout({
    type: FeatureRolloutType.Project,
    name: FeatureRolloutName.WebAnalyticsDashboardAndChannelClassifier,
    featureflag: ProjectFeatureFlag.WebAnalyticsDashboard,
    percentage: 100,
  }),
[FeatureRolloutName.PageDuration]: new FeatureRollout({
    type: FeatureRolloutType.Project,
    name: FeatureRolloutName.PageDuration,
    featureflag: ProjectFeatureFlag.PageDuration,
    percentage: 100,
  }),
};

export function useReportConsistency() {
  return isUrlDefinedExperimentalFeatureEnabled(window.location, `report_consistency`, () => {
    const projectId = getProjectId();
    const orgId = getOrgId(projectId);
    // explicitly only enable for P3. P5, P8 is not quite ready for this yet
    return orgId === 1 && parseInt(projectId, 10) === 3;
  });
}

export function useSingleReport(location: Location = window.location) {
  return isUrlDefinedExperimentalFeatureEnabled(location, `single_report`, false);
}

export function useTemplateSearch() {
  if (isTestEnv()) {
    return true;
  }
  return isUrlDefinedExperimentalFeatureEnabled(window.location, `template_search`, () => isCurrentProjectInternal());
}

export function isFoundationalTemplatesEnabled(currentProject) {
  if (!currentProject) {
    return false;
  }
  const isRolloutEnabled = featureSamplingUtils.determineFeatureEligibility(
    currentProject.organizationId.toString(),
    ProjectFeatureFlag.FoundationalTemplates,
    100, // @JakeW update rollout % for Foundational Templates here
  );
  return isRolloutEnabled || currentProject.hasFeatureFlag(ProjectFeatureFlag.FoundationalTemplates);
}

export function isRHSModeEnabled(currentProject: CurrentProject): boolean {
  return (
    isUrlDefinedExperimentalFeatureEnabled(window.location, `rhs-mode`, false) ||
    FeatureRollouts[FeatureRolloutName.RHSMode].isEnabled(currentProject)
  );
}

export function shouldShowHomePage(currentProject: CurrentProject, userId: number): boolean {
  // TODO(Home): Clean up after Home is 100% available.
  const HOME_BETA_TESTER_PROJECT_IDS = [3];
  return (
    isUrlDefinedExperimentalFeatureEnabled(window.location, `home-prototype`, false) ||
    FeatureRollouts[FeatureRolloutName.HomePrototype].isEnabled(currentProject) ||
    HOME_BETA_TESTER_USER_IDS.includes(userId) ||
    HOME_BETA_TESTER_PROJECT_IDS.includes(currentProject?.id)
  );
}

export const useInviteV2 = (orgId) => {
  return isDevEnv() || orgId === 1 || featureSamplingUtils.determineFeatureEligibility(orgId, `invite-modal-v2`, 50);
};
